import { apiFetch } from "./client.ts";

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

// --- Types ---
export interface Requirement {
  id: string; projectId: string; parentId: string | null; type: string; status: string;
  priority: string; title: string; description: string | null; acceptanceCriteria: string[];
  tags: string[]; version: number; createdBy: string; approvedBy: string | null;
  createdAt: string; updatedAt: string;
}

export interface Sprint {
  id: string; projectId: string; name: string; goal: string | null; status: string;
  startDate: string | null; endDate: string | null; velocity: number | null;
  version: number; createdBy: string; createdAt: string; updatedAt: string;
}

export interface TaskItem {
  id: string; projectId: string; sprintId: string | null; parentId: string | null;
  type: string; title: string; description: string | null; status: string; priority: string;
  assigneeId: string | null; labels: string[]; storyPoints: number | null; dueDate: string | null;
  sortOrder: number; version: number; createdBy: string; createdAt: string; updatedAt: string;
}

export interface Milestone {
  id: string; projectId: string; title: string; description: string | null;
  status: string; targetDate: string | null; reachedDate: string | null;
  version: number; createdBy: string; createdAt: string; updatedAt: string;
}

export interface Review {
  id: string; projectId: string; taskId: string | null; artifactType: string;
  artifactRef: string; title: string; status: string; reviewerId: string | null;
  approvedBy: string | null; comments: string | null; agentOutput: string | null;
  version: number; createdBy: string; createdAt: string; updatedAt: string;
}

export interface DocArtifact {
  id: string; projectId: string; sprintId: string | null; type: string; title: string;
  content: string; status: string; docVersion: number; generatedByAgent: string | null;
  reviewedBy: string | null; publishedBy: string | null;
  version: number; createdBy: string; createdAt: string; updatedAt: string;
}

export interface QaPlan {
  id: string; projectId: string; sprintId: string | null; title: string; status: string;
  releaseChecklist: Array<{ item: string; done: boolean }>;
  version: number; createdBy: string; createdAt: string; updatedAt: string;
}

export interface TestCase {
  id: string; qaPlanId: string; taskId: string | null; type: string; title: string;
  description: string | null; steps: string[] | null; expectedResult: string | null;
  status: string; generatedByAgent: string | null;
  version: number; createdAt: string; updatedAt: string;
}

export interface DeliveryOverview {
  requirements: Record<string, number>;
  tasks: Record<string, number>;
  sprints: Record<string, number>;
  reviews: Record<string, number>;
}

// --- Requirements ---
export const listRequirements = (token: string, p: { projectId?: string; type?: string; status?: string; search?: string; limit?: number; offset?: number } = {}) => {
  const s = new URLSearchParams();
  if (p.projectId) s.set("projectId", p.projectId);
  if (p.type) s.set("type", p.type);
  if (p.status) s.set("status", p.status);
  if (p.search) s.set("search", p.search);
  if (p.limit != null) s.set("limit", String(p.limit));
  if (p.offset != null) s.set("offset", String(p.offset));
  const q = s.toString();
  return apiFetch<Page<Requirement>>(`/api/v1/delivery/requirements${q ? `?${q}` : ""}`, { token });
};

export const createRequirement = (token: string, body: { projectId: string; type: string; priority: string; title: string; description?: string; acceptanceCriteria?: string[]; tags?: string[] }) =>
  apiFetch<Requirement>("/delivery/requirements", { token, method: "POST", body: JSON.stringify(body) });

export const approveRequirement = (token: string, id: string, version: number) =>
  apiFetch<Requirement>(`/api/v1/delivery/requirements/${id}/approve`, { token, method: "POST", body: JSON.stringify({ version }) });

export const rejectRequirement = (token: string, id: string, version: number) =>
  apiFetch<Requirement>(`/api/v1/delivery/requirements/${id}/reject`, { token, method: "POST", body: JSON.stringify({ version }) });

// --- Sprints ---
export const listSprints = (token: string, p: { projectId?: string; status?: string } = {}) => {
  const s = new URLSearchParams();
  if (p.projectId) s.set("projectId", p.projectId);
  if (p.status) s.set("status", p.status);
  const q = s.toString();
  return apiFetch<Page<Sprint>>(`/api/v1/delivery/sprints${q ? `?${q}` : ""}`, { token });
};

export const createSprint = (token: string, body: { projectId: string; name: string; goal?: string; startDate?: string; endDate?: string }) =>
  apiFetch<Sprint>("/delivery/sprints", { token, method: "POST", body: JSON.stringify(body) });

export const activateSprint = (token: string, id: string, version: number) =>
  apiFetch<Sprint>(`/api/v1/delivery/sprints/${id}/activate`, { token, method: "POST", body: JSON.stringify({ version }) });

export const completeSprint = (token: string, id: string, version: number, velocity?: number) =>
  apiFetch<Sprint>(`/api/v1/delivery/sprints/${id}/complete`, { token, method: "POST", body: JSON.stringify({ version, velocity }) });

// --- Tasks ---
export const listTasks = (token: string, p: { projectId?: string; sprintId?: string; type?: string; status?: string; search?: string; limit?: number; offset?: number } = {}) => {
  const s = new URLSearchParams();
  if (p.projectId) s.set("projectId", p.projectId);
  if (p.sprintId) s.set("sprintId", p.sprintId);
  if (p.type) s.set("type", p.type);
  if (p.status) s.set("status", p.status);
  if (p.search) s.set("search", p.search);
  if (p.limit != null) s.set("limit", String(p.limit));
  if (p.offset != null) s.set("offset", String(p.offset));
  const q = s.toString();
  return apiFetch<Page<TaskItem>>(`/api/v1/delivery/tasks${q ? `?${q}` : ""}`, { token });
};

export const createTask = (token: string, body: { projectId: string; sprintId?: string; type: string; title: string; priority: string; description?: string; storyPoints?: number }) =>
  apiFetch<TaskItem>("/delivery/tasks", { token, method: "POST", body: JSON.stringify(body) });

export const updateTaskStatus = (token: string, id: string, version: number, status: string) =>
  apiFetch<TaskItem>(`/api/v1/delivery/tasks/${id}`, { token, method: "PATCH", body: JSON.stringify({ version, status }) });

// --- Milestones ---
export const listMilestones = (token: string, p: { projectId?: string; status?: string } = {}) => {
  const s = new URLSearchParams();
  if (p.projectId) s.set("projectId", p.projectId);
  if (p.status) s.set("status", p.status);
  const q = s.toString();
  return apiFetch<Milestone[]>(`/api/v1/delivery/milestones${q ? `?${q}` : ""}`, { token });
};

export const createMilestone = (token: string, body: { projectId: string; title: string; description?: string; targetDate?: string }) =>
  apiFetch<Milestone>("/delivery/milestones", { token, method: "POST", body: JSON.stringify(body) });

export const reachMilestone = (token: string, id: string, version: number) =>
  apiFetch<Milestone>(`/api/v1/delivery/milestones/${id}/reach`, { token, method: "POST", body: JSON.stringify({ version }) });

// --- Reviews ---
export const listReviews = (token: string, p: { projectId?: string; status?: string; limit?: number; offset?: number } = {}) => {
  const s = new URLSearchParams();
  if (p.projectId) s.set("projectId", p.projectId);
  if (p.status) s.set("status", p.status);
  if (p.limit != null) s.set("limit", String(p.limit));
  if (p.offset != null) s.set("offset", String(p.offset));
  const q = s.toString();
  return apiFetch<Page<Review>>(`/api/v1/delivery/reviews${q ? `?${q}` : ""}`, { token });
};

export const approveReview = (token: string, id: string, version: number, comments?: string) =>
  apiFetch<Review>(`/api/v1/delivery/reviews/${id}/approve`, { token, method: "POST", body: JSON.stringify({ version, comments }) });

export const requestReviewChanges = (token: string, id: string, version: number, comments: string) =>
  apiFetch<Review>(`/api/v1/delivery/reviews/${id}/request-changes`, { token, method: "POST", body: JSON.stringify({ version, comments }) });

// --- Docs ---
export const listDocs = (token: string, p: { projectId?: string; type?: string; status?: string; limit?: number; offset?: number } = {}) => {
  const s = new URLSearchParams();
  if (p.projectId) s.set("projectId", p.projectId);
  if (p.type) s.set("type", p.type);
  if (p.status) s.set("status", p.status);
  if (p.limit != null) s.set("limit", String(p.limit));
  if (p.offset != null) s.set("offset", String(p.offset));
  const q = s.toString();
  return apiFetch<Page<DocArtifact>>(`/api/v1/delivery/docs${q ? `?${q}` : ""}`, { token });
};

export const publishDoc = (token: string, id: string, version: number) =>
  apiFetch<DocArtifact>(`/api/v1/delivery/docs/${id}/publish`, { token, method: "POST", body: JSON.stringify({ version }) });

// --- QA ---
export const listQaPlans = (token: string, p: { projectId?: string; sprintId?: string } = {}) => {
  const s = new URLSearchParams();
  if (p.projectId) s.set("projectId", p.projectId);
  if (p.sprintId) s.set("sprintId", p.sprintId);
  const q = s.toString();
  return apiFetch<QaPlan[]>(`/api/v1/delivery/qa/plans${q ? `?${q}` : ""}`, { token });
};

export const listTestCases = (token: string, p: { qaPlanId?: string; status?: string } = {}) => {
  const s = new URLSearchParams();
  if (p.qaPlanId) s.set("qaPlanId", p.qaPlanId);
  if (p.status) s.set("status", p.status);
  const q = s.toString();
  return apiFetch<{ items: TestCase[]; total: number }>(`/api/v1/delivery/qa/test-cases${q ? `?${q}` : ""}`, { token });
};

export const releaseQaPlan = (token: string, id: string, version: number) =>
  apiFetch<QaPlan>(`/api/v1/delivery/qa/plans/${id}/release`, { token, method: "POST", body: JSON.stringify({ version }) });

// --- Analytics ---
export const getDeliveryOverview = (token: string, projectId?: string) => {
  const q = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
  return apiFetch<DeliveryOverview>(`/api/v1/delivery/analytics/overview${q}`, { token });
};
